/**
 * @fileoverview Violation / occurrence query helpers over the canonical
 *   response model. Under that model there is NO top-level
 *   `case.violations[]` collection — violations live nested inside the
 *   response that raised them (`response.violation`). These helpers provide
 *   the flattened/list views UIs expect without reintroducing a top-level
 *   collection that would drift out of sync.
 * @module core-domain/src/inspection/violationQueries
 *
 * Responsibilities:
 * - Flatten/list/count violations and occurrences from `case.responses{}`.
 * - Resolve a violation or occurrence (and its owning response) by id.
 * - Classify and surface additional (ad-hoc, inspector-added) violations via
 *   the `ADDITIONAL_VIOLATION_FORM_ID` sentinel.
 * - Build the cross-case `violationsViewPipeline` MongoDB aggregation.
 *
 * Dependencies:
 * - ./responseTypes (CanonicalQuestionResponse, Violation, ViolationOccurrence)
 * - ./caseQueries (CanonicalCaseShape)
 *
 * Architecture:
 * - Pure, side-effect-free read helpers. No I/O, no Dexie, no API; safe to call
 *   from any platform layer on either app. The shared traversal primitives
 *   (`getAllViolations` / `getAllOccurrences`) back the higher-level domain
 *   predicates in `./violationResolution` so traversal stays DRY.
 *
 * Notes:
 * - `violationsViewPipeline` returns loosely-typed aggregation stages
 *   (`unknown[]`) on purpose, to avoid pulling a MongoDB type dependency into
 *   `@firesquirrel/core-domain`. It is not materialized today (no
 *   `violationsView` collection); it is a pure function a future Atlas Trigger
 *   can wrap for an idempotent rebuild.
 */

import type {
  CanonicalQuestionResponse,
  Violation,
  ViolationOccurrence,
} from './responseTypes';
import type { CanonicalCaseShape } from './caseQueries';

// ---------------------------------------------------------------------------
// Additional-violation sentinels
//
// Ad-hoc violations added by an inspector — violations with no underlying
// template question — live on a synthetic `CanonicalQuestionResponse`
// keyed by `ADDITIONAL_VIOLATION_FORM_ID`. The synthetic response carries
// the full canonical plumbing (attachments, transitions, etc.) so the rest
// of the pipeline treats them uniformly with question-sourced violations.
//
// See the "Additional violations" section of the canonical-response-model
// plan for the complete contract.
// ---------------------------------------------------------------------------

/** Reserved sentinel formId. No real template form may use this value. */
export const ADDITIONAL_VIOLATION_FORM_ID = '__additional__';

/** Reserved sentinel widgetType. No real widget may use this value. */
export const ADDITIONAL_VIOLATION_WIDGET_TYPE = 'additionalViolation';

/**
 * Build the canonical synthetic fieldId for an additional-violation
 * response. Deterministic: `addl-${violation._id}`. The response key equals
 * this string, so `case.responses[buildAdditionalViolationFieldId(v._id)]`
 * always resolves the owning response in O(1).
 */
export function buildAdditionalViolationFieldId(violationId: string): string {
  return `addl-${violationId}`;
}

/**
 * Classifier. Returns true when the response is the synthetic wrapper for
 * an additional violation. Implementation keys off the sentinel `formId`
 * (the authoritative hint for the response side), NOT off the synthetic
 * fieldId prefix, to keep the two sentinels coupled.
 */
export function isAdditionalViolationResponse(
  response: Pick<CanonicalQuestionResponse, 'formId'>,
): boolean {
  return response.formId === ADDITIONAL_VIOLATION_FORM_ID;
}

// ---------------------------------------------------------------------------
// Violation collection helpers
// ---------------------------------------------------------------------------

/**
 * Return every violation on the case, flattened from the canonical
 * response map. Order is stable: by formId, then by fieldId, then by
 * violation._id.
 */
export function getAllViolations(caseDoc: CanonicalCaseShape): Violation[] {
  if (!caseDoc.responses) return [];
  const out: Array<{ v: Violation; key: string }> = [];
  for (const response of Object.values(caseDoc.responses)) {
    if (!response.violation) continue;
    // G-101 defensive filter: skip violations on N/A responses. The UI
    // guard prevents toggling N/A while occurrences exist, but legacy data
    // or direct writes could leave orphaned violations on N/A responses.
    if (response.isNA) continue;
    const key = `${response.formId}::${response.fieldId}::${response.violation._id}`;
    out.push({ v: response.violation, key });
  }
  return out
    .sort((a, b) => a.key.localeCompare(b.key))
    .map((x) => x.v);
}

/**
 * Return only the violations currently considered open (status !== 'closed'
 * and status !== 'resolved'). Useful for conductor dashboards.
 */
export function getOpenViolations(caseDoc: CanonicalCaseShape): Violation[] {
  return getAllViolations(caseDoc).filter(
    (v) => v.status !== 'closed' && v.status !== 'resolved',
  );
}

/**
 * Find a single violation by its `_id`. Returns `undefined` when no
 * response on the case currently raises that violation.
 */
export function getViolationById(
  caseDoc: CanonicalCaseShape,
  violationId: string,
): Violation | undefined {
  if (!caseDoc.responses) return undefined;
  for (const response of Object.values(caseDoc.responses)) {
    if (response.violation?._id === violationId) return response.violation;
  }
  return undefined;
}

/**
 * Return the response that currently raises a given violation. Useful when
 * a UI lists violations and needs to jump back to the owning widget.
 */
export function findResponseForViolation(
  caseDoc: CanonicalCaseShape,
  violationId: string,
): CanonicalQuestionResponse | undefined {
  if (!caseDoc.responses) return undefined;
  for (const response of Object.values(caseDoc.responses)) {
    if (response.violation?._id === violationId) return response;
  }
  return undefined;
}

/**
 * Return every violation raised by responses on a given formId.
 */
export function getViolationsByFormId(
  caseDoc: CanonicalCaseShape,
  formId: string,
): Violation[] {
  if (!caseDoc.responses) return [];
  const out: Violation[] = [];
  for (const response of Object.values(caseDoc.responses)) {
    if (response.formId !== formId) continue;
    // G-101 defensive filter: consistent with getAllViolations.
    if (response.isNA) continue;
    if (response.violation) out.push(response.violation);
  }
  return out;
}

/**
 * Return the synthetic responses for every additional (ad-hoc) violation
 * on the case — those with `formId === ADDITIONAL_VIOLATION_FORM_ID`.
 * UIs that want to render "inspector-added violations" iterate this list.
 */
export function getAdditionalViolationResponses(
  caseDoc: CanonicalCaseShape,
): CanonicalQuestionResponse[] {
  if (!caseDoc.responses) return [];
  const out: CanonicalQuestionResponse[] = [];
  for (const response of Object.values(caseDoc.responses)) {
    // G-101 defensive filter: consistent with getAllViolations.
    if (response.isNA) continue;
    if (isAdditionalViolationResponse(response)) out.push(response);
  }
  return out;
}

/**
 * Convenience — only the violations attached to additional-violation
 * responses. Equivalent to `getAllViolations(caseDoc).filter(v => v.source === 'additional')`
 * but walks `responses{}` once and uses the sentinel for classification
 * to keep the two source-of-truths coupled.
 */
export function getAdditionalViolations(
  caseDoc: CanonicalCaseShape,
): Violation[] {
  const out: Violation[] = [];
  for (const response of getAdditionalViolationResponses(caseDoc)) {
    if (response.violation) out.push(response.violation);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Occurrence helpers — flattened across all violations on the case.
// ---------------------------------------------------------------------------

export interface OccurrenceWithContext {
  occurrence: ViolationOccurrence;
  violation: Violation;
  response: CanonicalQuestionResponse;
}

/**
 * Flatten every occurrence across every violation on the case, each paired
 * with its owning violation and response for downstream UIs that need
 * parent context (e.g. "show this photo under form X, widget Y").
 */
export function getAllOccurrences(
  caseDoc: CanonicalCaseShape,
): OccurrenceWithContext[] {
  if (!caseDoc.responses) return [];
  const out: OccurrenceWithContext[] = [];
  for (const response of Object.values(caseDoc.responses)) {
    const violation = response.violation;
    if (!violation) continue;
    // G-101 defensive filter: skip occurrences on N/A responses.
    if (response.isNA) continue;
    for (const occurrence of violation.occurrences ?? []) {
      out.push({ occurrence, violation, response });
    }
  }
  return out;
}

/**
 * Only occurrences with status `'open'` — what the inspector still needs
 * to verify on a reinspection.
 */
export function getOpenOccurrences(
  caseDoc: CanonicalCaseShape,
): OccurrenceWithContext[] {
  return getAllOccurrences(caseDoc).filter(
    (oc) => oc.occurrence.status === 'open',
  );
}

/**
 * Locate a specific occurrence by its `_id`, returning it alongside its
 * owning violation and response. Returns `undefined` when the occurrence
 * is not present on the case.
 */
export function getOccurrenceById(
  caseDoc: CanonicalCaseShape,
  occurrenceId: string,
): OccurrenceWithContext | undefined {
  if (!caseDoc.responses) return undefined;
  for (const response of Object.values(caseDoc.responses)) {
    const violation = response.violation;
    if (!violation) continue;
    for (const occurrence of violation.occurrences ?? []) {
      if (occurrence._id === occurrenceId) {
        return { occurrence, violation, response };
      }
    }
  }
  return undefined;
}

/**
 * Total count of violations on the case. Common dashboard signal.
 */
export function countViolations(caseDoc: CanonicalCaseShape): number {
  return getAllViolations(caseDoc).length;
}

/**
 * Total count of occurrences on the case. Common reporting signal.
 */
export function countOccurrences(caseDoc: CanonicalCaseShape): number {
  return getAllOccurrences(caseDoc).length;
}

// ---------------------------------------------------------------------------
// violationsViewPipeline — cross-case MongoDB aggregation
//
// Per canonical-response-model plan §511-531, entity-wide / cross-case
// violation views are materialized via this aggregation against the
// `cases` collection. The pipeline:
//   1. Matches the caller-supplied filter (e.g. `{ entityId }`).
//   2. Flattens the response map into an array keyed by fieldId.
//   3. Keeps only responses carrying a nested `violation`.
//   4. Projects the canonical view shape every consumer expects.
//
// Not materialized today — the plan defers a `violationsView` collection
// until dashboard performance demands it. The pipeline is a pure function
// so a future Atlas Trigger can wrap it for an idempotent rebuild.
// ---------------------------------------------------------------------------

/**
 * Canonical shape of a row produced by `violationsViewPipeline`. One row
 * per response that carries a violation, across any cases that match the
 * filter.
 */
export interface ViolationsViewRow {
  caseId: string;
  entityId?: string;
  fieldId: string;
  questionText?: string;
  violation: Violation;
}

/**
 * Aggregation stages are intentionally typed loosely — callers pass the
 * result directly to `collection.aggregate(...)` in whichever driver they
 * use. Keeping `unknown[]` avoids pulling a MongoDB type dependency into
 * `@firesquirrel/core-domain`.
 */
export type AggregationStage = Record<string, unknown>;

/**
 * Build the cross-case violations aggregation for the given filter.
 * Filter shape is passed through as the `$match` stage; pass
 * `{ entityId: '...' }` to scope to a single entity or `{}` to see every
 * case in the collection.
 */
export function violationsViewPipeline(
  filter: Record<string, unknown> = {},
): AggregationStage[] {
  return [
    { $match: filter },
    {
      $project: {
        entityId: 1,
        responses: { $objectToArray: '$responses' },
      },
    },
    { $unwind: '$responses' },
    { $match: { 'responses.v.violation': { $exists: true }, 'responses.v.isNA': { $ne: true } } },
    {
      $project: {
        _id: 0,
        caseId: '$_id',
        entityId: 1,
        fieldId: '$responses.k',
        questionText: '$responses.v.questionText',
        violation: '$responses.v.violation',
      },
    },
  ];
}
